// Les RÉDUCTEURS : `etat_suivant = appliquer(etat, evenement)`.
// C'est ici que vivent TOUTES les règles de transformation de l'état, en un seul
// endroit, exécuté à l'identique dans le navigateur et sur le serveur
// (docs/architecture.md §4, docs/specs-fonctionnelles.md §7).
import type { Etat, ProgressionSession, EntreeJournal } from './types';
import { etatInitial, etudeCasVide } from './types';
import type { Evenement } from './evenements';
import { nouvelleCarte, reviser } from './sm2';
import { jourPedagogique } from './dates';

/** Progression d'une session, créée à la demande. */
function session(etat: Etat, id: string): ProgressionSession {
  let p = etat.progression.sessions[id];
  if (!p) {
    p = { statut: 'a_faire', etape_courante: 0, etapes_faites: [], capsules_lues: [], mode: 'standard', faible_energie: 0 };
    etat.progression.sessions[id] = p;
  }
  return p;
}

/** Position suivante dans une colonne du tableau. */
function positionSuivante(etat: Etat, colonne: string): number {
  const dans = Object.values(etat.tableau.elements).filter((e) => e.colonne === colonne);
  return dans.length === 0 ? 0 : Math.max(...dans.map((e) => e.position)) + 1;
}

/**
 * Applique UN événement à un état, EN LE MODIFIANT (pour rejouer vite des milliers
 * d'événements). Utiliser `reduireTout` pour ne pas toucher à l'état d'origine.
 * Un événement inconnu est ignoré : l'état ne doit jamais être corrompu par le futur.
 */
export function appliquer(etat: Etat, ev: Evenement): Etat {
  const quand = ev.cree_le;
  const aujourdhui = jourPedagogique(new Date(quand));

  switch (ev.type) {
    case 'saison_activee': {
      const { saison, debut, mode_agile, livrable } = ev.charge;
      etat.saison_courante = Math.max(etat.saison_courante, saison);
      etat.parametres.debut_saisons[String(saison)] = debut;
      etat.tableau.mode = mode_agile;
      if (livrable) {
        const idProjet = `livrable:S${saison}`;
        if (!etat.portfolio.some((p) => p.id === idProjet)) {
          etat.portfolio.push({ id: idProjet, titre: livrable.titre, description: livrable.description, saison, statut: 'en_cours', captures: [], cree_le: quand });
        }
        const idElement = `livrable:S${saison}`;
        if (!etat.tableau.elements[idElement]) {
          etat.tableau.elements[idElement] = { id: idElement, titre: livrable.titre, description: livrable.description, colonne: 'a_faire', position: positionSuivante(etat, 'a_faire'), source: 'livrable', cree_le: quand };
          etat.tableau.backlog_ordre.push(idElement);
        }
        livrable.jalons.forEach((jalon, i) => {
          const idJalon = `jalon:S${saison}:${i + 1}`;
          if (!etat.tableau.elements[idJalon]) {
            etat.tableau.elements[idJalon] = { id: idJalon, titre: jalon, colonne: 'a_faire', position: positionSuivante(etat, 'a_faire'), source: 'jalon', cree_le: quand };
            etat.tableau.backlog_ordre.push(idJalon);
          }
        });
      }
      return etat;
    }

    case 'saison_suivante': {
      etat.saison_courante = ev.charge.saison;
      return etat;
    }

    // R-PAR-02/05 : un parcours annexe se lance quand on veut ; son livrable et ses jalons
    // rejoignent le portfolio et le tableau, comme ceux d'une saison. Rien d'autre ne change :
    // ses sessions ont des identifiants à elles (`EL-03`) et passent par les mêmes réducteurs
    // que celles d'une saison. Voir ADR 0009.
    case 'parcours_active': {
      const { parcours, livrable } = ev.charge;
      if (etat.parcours[parcours]) return etat; // idempotent
      etat.parcours[parcours] = { active_le: quand };
      if (livrable) {
        const idProjet = `livrable:${parcours}`;
        if (!etat.portfolio.some((p) => p.id === idProjet)) {
          etat.portfolio.push({ id: idProjet, titre: livrable.titre, description: livrable.description, saison: 0, parcours, statut: 'en_cours', captures: [], cree_le: quand });
        }
        if (!etat.tableau.elements[idProjet]) {
          etat.tableau.elements[idProjet] = { id: idProjet, titre: livrable.titre, description: livrable.description, colonne: 'a_faire', position: positionSuivante(etat, 'a_faire'), source: 'livrable', cree_le: quand };
          etat.tableau.backlog_ordre.push(idProjet);
        }
        livrable.jalons.forEach((jalon, i) => {
          const idJalon = `jalon:${parcours}:${i + 1}`;
          if (!etat.tableau.elements[idJalon]) {
            etat.tableau.elements[idJalon] = { id: idJalon, titre: jalon, colonne: 'a_faire', position: positionSuivante(etat, 'a_faire'), source: 'jalon', cree_le: quand };
            etat.tableau.backlog_ordre.push(idJalon);
          }
        });
      }
      return etat;
    }

    case 'session_commencee': {
      // Une seule session en cours à la fois (R-SES-11) : les autres passent en « abandonnee ».
      for (const [id, p] of Object.entries(etat.progression.sessions)) {
        if (id !== ev.charge.session && p.statut === 'en_cours') p.statut = 'abandonnee';
      }
      const p = session(etat, ev.charge.session);
      if (p.statut !== 'terminee') {
        p.statut = 'en_cours';
        p.mode = ev.charge.mode;
        p.commencee_le = p.commencee_le ?? quand;
      }
      // L'exercice de la session apparaît dans le tableau (R-TAB-02).
      const idElement = `exercice:${ev.charge.session}`;
      if (!etat.tableau.elements[idElement]) {
        etat.tableau.elements[idElement] = { id: idElement, titre: ev.charge.titre_exercice ?? `Exercice ${ev.charge.session}`, colonne: 'en_cours', position: positionSuivante(etat, 'en_cours'), source: 'exercice', session: ev.charge.session, cree_le: quand };
        etat.tableau.backlog_ordre.push(idElement);
      }
      return etat;
    }

    case 'etape_terminee': {
      const p = session(etat, ev.charge.session);
      if (!p.etapes_faites.includes(ev.charge.index)) p.etapes_faites.push(ev.charge.index);
      p.etape_courante = Math.max(p.etape_courante, ev.charge.index + 1);
      return etat;
    }

    case 'etape_reouverte': {
      const p = session(etat, ev.charge.session);
      p.etapes_faites = p.etapes_faites.filter((i) => i !== ev.charge.index);
      p.etape_courante = Math.min(p.etape_courante, ev.charge.index);
      return etat;
    }

    case 'capsule_lue': {
      const p = session(etat, ev.charge.session);
      if (!p.capsules_lues.includes(ev.charge.capsule)) p.capsules_lues.push(ev.charge.capsule);
      return etat;
    }

    case 'session_terminee': {
      const p = session(etat, ev.charge.session);
      if (p.statut === 'terminee') return etat; // idempotent
      p.statut = 'terminee';
      p.terminee_le = quand;
      p.duree_min = ev.charge.duree_min;
      etat.progression.realisations.push({ session: ev.charge.session, mode: 'standard', le: quand });
      // Les cartes de la session deviennent actives (R-CAR-02).
      for (const carte of ev.charge.cartes) {
        if (!etat.cartes[carte]) etat.cartes[carte] = nouvelleCarte(aujourdhui);
      }
      // L'entrée de journal de clôture (R-JRN-01).
      ajouterEntree(etat, { id: ev.charge.entree_journal, le: quand, type: 'cloture', texte: ev.charge.texte, session: ev.charge.session, energie: ev.charge.energie, archivee: false });
      // L'exercice passe en « fait » (R-TAB-02).
      const element = etat.tableau.elements[`exercice:${ev.charge.session}`];
      if (element && element.colonne !== 'fait') { element.colonne = 'fait'; element.position = positionSuivante(etat, 'fait'); }
      return etat;
    }

    case 'session_faible_energie_terminee': {
      const p = session(etat, ev.charge.session);
      p.faible_energie += 1;
      etat.progression.realisations.push({ session: ev.charge.session, mode: 'faible_energie', le: quand });
      ajouterEntree(etat, { id: ev.charge.entree_journal, le: quand, type: 'cloture', texte: ev.charge.texte, session: ev.charge.session, archivee: false });
      return etat;
    }

    case 'session_abandonnee': {
      const p = session(etat, ev.charge.session);
      if (p.statut === 'en_cours') p.statut = 'abandonnee';
      return etat;
    }

    case 'blocage_declenche': {
      const existant = etat.blocages.find((b) => b.exercice === ev.charge.exercice && b.session === ev.charge.session);
      if (!existant) etat.blocages.push({ exercice: ev.charge.exercice, session: ev.charge.session, t0: quand, paliers: [1] });
      return etat;
    }

    case 'palier_consulte': {
      let b = etat.blocages.find((x) => x.exercice === ev.charge.exercice && x.session === ev.charge.session);
      if (!b) { b = { exercice: ev.charge.exercice, session: ev.charge.session, t0: quand, paliers: [] }; etat.blocages.push(b); }
      if (!b.paliers.includes(ev.charge.palier)) b.paliers.push(ev.charge.palier);
      return etat;
    }

    case 'abandon_exercice': {
      let b = etat.blocages.find((x) => x.exercice === ev.charge.exercice && x.session === ev.charge.session);
      if (!b) { b = { exercice: ev.charge.exercice, session: ev.charge.session, t0: quand, paliers: [] }; etat.blocages.push(b); }
      b.abandon_le = b.abandon_le ?? quand;
      if (!b.paliers.includes(4)) b.paliers.push(4);
      // La variante est planifiée dans la prochaine session (R-BLQ-06).
      if (ev.charge.prochaine_session && !etat.variantes.some((v) => v.exercice === ev.charge.exercice)) {
        etat.variantes.push({ exercice: ev.charge.exercice, pour_session: ev.charge.prochaine_session, statut: 'planifiee' });
      }
      return etat;
    }

    case 'variante_terminee': {
      const v = etat.variantes.find((x) => x.exercice === ev.charge.exercice);
      if (v) v.statut = 'terminee';
      return etat;
    }

    case 'carte_revisee': {
      const carte = etat.cartes[ev.charge.carte];
      if (!carte) return etat;
      etat.cartes[ev.charge.carte] = reviser(carte, ev.charge.note, aujourdhui);
      etat.revisions.push({ carte: ev.charge.carte, note: ev.charge.note, le: quand });
      return etat;
    }

    case 'carte_creee': {
      if (!etat.cartes[ev.charge.carte]) {
        etat.cartes[ev.charge.carte] = nouvelleCarte(aujourdhui, { manuelle: true, recto: ev.charge.recto, verso: ev.charge.verso, domaine: ev.charge.domaine });
      }
      return etat;
    }

    case 'carte_suspendue': {
      const carte = etat.cartes[ev.charge.carte];
      if (carte) carte.suspendue = true;
      return etat;
    }

    case 'carte_reactivee': {
      const carte = etat.cartes[ev.charge.carte];
      if (carte) carte.suspendue = false;
      return etat;
    }

    case 'entree_journal_ajoutee': {
      ajouterEntree(etat, { id: ev.charge.entree, le: quand, type: ev.charge.type, texte: ev.charge.texte, session: ev.charge.session, sprint: ev.charge.sprint, energie: ev.charge.energie, archivee: false });
      return etat;
    }

    case 'entree_journal_modifiee': {
      const e = etat.journal.find((x) => x.id === ev.charge.entree);
      if (e) { e.texte = ev.charge.texte; e.modifiee_le = quand; }
      return etat;
    }

    case 'entree_journal_archivee': {
      const e = etat.journal.find((x) => x.id === ev.charge.entree);
      if (e) e.archivee = true;
      return etat;
    }

    case 'note_mentor_ajoutee': {
      ajouterEntree(etat, { id: ev.charge.entree, le: quand, type: 'note_mentor', sous_type: ev.charge.sous_type, texte: ev.charge.texte, session: ev.charge.session, archivee: false });
      return etat;
    }

    case 'element_cree': {
      if (!etat.tableau.elements[ev.charge.element]) {
        etat.tableau.elements[ev.charge.element] = {
          id: ev.charge.element, titre: ev.charge.titre, description: ev.charge.description, domaine: ev.charge.domaine,
          colonne: ev.charge.colonne, position: positionSuivante(etat, ev.charge.colonne), source: ev.charge.source, session: ev.charge.session,
          story: ev.charge.story, criteres: ev.charge.criteres?.map((texte) => ({ texte, fait: false })), cree_le: quand,
        };
        etat.tableau.backlog_ordre.push(ev.charge.element);
      }
      return etat;
    }

    case 'element_modifie': {
      const e = etat.tableau.elements[ev.charge.element];
      if (!e) return etat;
      if (ev.charge.titre !== undefined) e.titre = ev.charge.titre;
      if (ev.charge.description !== undefined) e.description = ev.charge.description;
      if (ev.charge.domaine !== undefined) e.domaine = ev.charge.domaine;
      if (ev.charge.story !== undefined) e.story = ev.charge.story;
      if (ev.charge.criteres !== undefined) e.criteres = ev.charge.criteres;
      return etat;
    }

    case 'element_deplace': {
      const e = etat.tableau.elements[ev.charge.element];
      if (!e) return etat;
      // On décale les éléments de la colonne cible pour faire de la place.
      for (const autre of Object.values(etat.tableau.elements)) {
        if (autre.id !== e.id && autre.colonne === ev.charge.colonne && autre.position >= ev.charge.position) autre.position += 1;
      }
      e.colonne = ev.charge.colonne;
      e.position = ev.charge.position;
      return etat;
    }

    case 'element_supprime': {
      delete etat.tableau.elements[ev.charge.element];
      etat.tableau.backlog_ordre = etat.tableau.backlog_ordre.filter((id) => id !== ev.charge.element);
      for (const s of etat.tableau.sprints) s.elements = s.elements.filter((id) => id !== ev.charge.element);
      return etat;
    }

    case 'sprint_cree': {
      if (!etat.tableau.sprints.some((s) => s.id === ev.charge.sprint)) {
        etat.tableau.sprints.push({ id: ev.charge.sprint, numero: ev.charge.numero, objectif: ev.charge.objectif, debut: ev.charge.debut, fin: ev.charge.fin, elements: [], statut: 'planifie' });
      }
      return etat;
    }

    case 'sprint_demarre': {
      for (const s of etat.tableau.sprints) if (s.statut === 'en_cours') s.statut = 'termine';
      const s = etat.tableau.sprints.find((x) => x.id === ev.charge.sprint);
      if (s) s.statut = 'en_cours';
      return etat;
    }

    case 'sprint_termine': {
      const s = etat.tableau.sprints.find((x) => x.id === ev.charge.sprint);
      if (s) s.statut = 'termine';
      return etat;
    }

    case 'element_engage': {
      const s = etat.tableau.sprints.find((x) => x.id === ev.charge.sprint);
      if (!s) return etat;
      s.elements = s.elements.filter((id) => id !== ev.charge.element);
      if (ev.charge.engage) s.elements.push(ev.charge.element);
      const e = etat.tableau.elements[ev.charge.element];
      if (e) e.sprint = ev.charge.engage ? ev.charge.sprint : undefined;
      return etat;
    }

    case 'defi_marque': {
      etat.progression.defis[ev.charge.defi] = { statut: ev.charge.statut, fait_le: ev.charge.statut === 'fait' ? quand : undefined };
      return etat;
    }

    case 'soutenance_marquee': {
      etat.progression.soutenances[ev.charge.soutenance] = { statut: ev.charge.statut, faite_le: ev.charge.statut === 'faite' ? quand : undefined };
      return etat;
    }

    case 'projet_cree': {
      if (!etat.portfolio.some((p) => p.id === ev.charge.projet)) {
        etat.portfolio.push({ id: ev.charge.projet, titre: ev.charge.titre, description: ev.charge.description, saison: ev.charge.saison, statut: 'en_cours', depot: ev.charge.depot, demo: ev.charge.demo, captures: [], cree_le: quand });
      }
      return etat;
    }

    case 'projet_modifie': {
      const p = etat.portfolio.find((x) => x.id === ev.charge.projet);
      if (!p) return etat;
      if (ev.charge.titre !== undefined) p.titre = ev.charge.titre;
      if (ev.charge.description !== undefined) p.description = ev.charge.description;
      if (ev.charge.statut !== undefined) p.statut = ev.charge.statut;
      if (ev.charge.depot !== undefined) p.depot = ev.charge.depot || undefined;
      if (ev.charge.demo !== undefined) p.demo = ev.charge.demo || undefined;
      // Le livrable de saison, livré, passe aussi en « fait » au tableau.
      const element = etat.tableau.elements[p.id];
      if (element && ev.charge.statut === 'livre' && element.colonne !== 'fait') { element.colonne = 'fait'; element.position = positionSuivante(etat, 'fait'); }
      return etat;
    }

    // R-PTF-05 : le bloc étude de cas se remplit par petits bouts, souvent sur plusieurs soirs.
    case 'etude_cas_modifiee': {
      const p = etat.portfolio.find((x) => x.id === ev.charge.projet);
      if (!p) return etat;
      const etude = (p.etude_cas ??= etudeCasVide());
      if (ev.charge.readme !== undefined) etude.readme = ev.charge.readme;
      if (ev.charge.script_demo !== undefined) etude.script_demo = ev.charge.script_demo;
      if (ev.charge.post !== undefined) etude.post = ev.charge.post;
      // On ne redescend jamais de « publiée » : ce qui est publié l'est.
      if (ev.charge.statut !== undefined && etude.statut !== 'publie') etude.statut = ev.charge.statut;
      if (ev.charge.checklist) Object.assign(etude.checklist, ev.charge.checklist);
      return etat;
    }

    /*
     * R-PTF-06 : la checklist est BLOQUANTE. L'interface empêche déjà le clic, mais l'état
     * refuse aussi — un événement peut arriver d'un autre appareil, d'une boîte d'envoi
     * ancienne, ou d'un appel direct à l'API. Un secret publié ne se dépublie pas.
     */
    case 'etude_cas_publiee': {
      const p = etat.portfolio.find((x) => x.id === ev.charge.projet);
      const etude = p?.etude_cas;
      if (!etude) return etat;
      const { secrets, donnees, infra, relu } = etude.checklist;
      if (!(secrets && donnees && infra && relu)) return etat;
      etude.statut = 'publie';
      etude.publiee_le = quand;
      return etat;
    }

    // R-VEI-01/03 : 20 minutes une fois par mois. Compte dans la régularité comme une séance
    // faible énergie, ne consomme aucune session de progression.
    case 'veille_ajoutee': {
      if (etat.veille.entrees.some((e) => e.id === ev.charge.entree)) return etat;
      etat.veille.entrees.push({ id: ev.charge.entree, le: quand, offres: ev.charge.offres, competences: ev.charge.competences });
      etat.progression.realisations.push({ session: `veille:${ev.charge.entree}`, mode: 'faible_energie', le: quand });
      for (const competence of ev.charge.competences) {
        const ecart = (etat.veille.ecarts[competence] ??= { couverture: 'absente', vu_le: [] });
        if (!ecart.vu_le.includes(aujourdhui)) ecart.vu_le.push(aujourdhui);
      }
      return etat;
    }

    case 'ecart_marque': {
      const ecart = (etat.veille.ecarts[ev.charge.competence] ??= { couverture: 'absente', vu_le: [] });
      ecart.couverture = ev.charge.couverture;
      ecart.saison = ev.charge.saison;
      return etat;
    }

    case 'capture_ajoutee': {
      const p = etat.portfolio.find((x) => x.id === ev.charge.projet);
      if (p && !p.captures.includes(ev.charge.fichier) && p.captures.length < 5) p.captures.push(ev.charge.fichier);
      return etat;
    }

    case 'capture_retiree': {
      const p = etat.portfolio.find((x) => x.id === ev.charge.projet);
      if (p) p.captures = p.captures.filter((f) => f !== ev.charge.fichier);
      return etat;
    }

    case 'parametres_modifies': {
      const c = ev.charge;
      if (c.jours) etat.parametres.jours = [...new Set(c.jours)].sort();
      if (c.vendredi !== undefined) etat.parametres.vendredi = c.vendredi;
      if (c.debut_saisons) Object.assign(etat.parametres.debut_saisons, c.debut_saisons);
      if (c.faible_energie_defaut !== undefined) etat.parametres.faible_energie_defaut = c.faible_energie_defaut;
      if (c.theme) etat.parametres.theme = c.theme;
      return etat;
    }

    case 'semaine_off_marquee': {
      if (!etat.semaines_off.includes(ev.charge.semaine)) etat.semaines_off.push(ev.charge.semaine);
      return etat;
    }

    case 'semaine_off_retiree': {
      etat.semaines_off = etat.semaines_off.filter((s) => s !== ev.charge.semaine);
      return etat;
    }

    default:
      return etat;
  }
}

function ajouterEntree(etat: Etat, entree: EntreeJournal) {
  if (!etat.journal.some((e) => e.id === entree.id)) etat.journal.push(entree);
}

/** Applique une liste d'événements SANS modifier l'état de départ (copie unique au début). */
export function reduireTout(etat: Etat, evenements: readonly Evenement[]): Etat {
  const copie = structuredClone(etat);
  for (const ev of evenements) appliquer(copie, ev);
  return copie;
}

/** Applique un seul événement sans modifier l'état de départ. */
export function reduire(etat: Etat, evenement: Evenement): Etat {
  return reduireTout(etat, [evenement]);
}

/** Reconstruit l'état complet depuis zéro (utilisé par le serveur au démarrage et par le script de rejeu). */
export function rejouer(evenements: readonly Evenement[]): Etat {
  return reduireTout(etatInitial(), evenements);
}
